import { Component, OnInit } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';
import { User } from 'src/app/models/user';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MixpanelService } from 'src/app/services/mixpanel/mixpanel.service';
import { DocumentService } from 'src/app/services/document/document.service';
import { SectionBaseline } from 'src/app/models/section-baseline';
import { Country } from 'src/app/models/country';
import { CountryService } from 'src/app/services/country/country.service';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'app-preferences',
  templateUrl: './preferences.component.html',
  styleUrls: ['./preferences.component.scss'],
})
export class PreferencesComponent implements OnInit {
  public notificationFrequency = 'monthly';
  public message: string;
  public error: string;
  private user: User;
  public availableSections: SectionBaseline[] = [];
  public selectedNotificationSectionIds: number[] = [];
  private allSectionIds: number[] = [];
  private prevAllSectionsToggled = false;
  private prevSelectedSectionIds: number[] = [];
  public availableCountries: Country[] = [];
  public selectedNotificationCountries: Country[] = [];

  public onNotificationSectionChange(event: MatSelectChange) {
    const newValues: number[] = event.value;
    const nowHasAll = newValues.includes(0);
    const hadAll = this.prevAllSectionsToggled;

    let result: number[];

    if (nowHasAll && !hadAll) {
      // "All sections" toggled on
      result = [0, ...this.allSectionIds];
    } else if (!nowHasAll && hadAll) {
      // "All sections" toggled off
      result = [];
    } else {
      // Individual item toggled — strip ALL sentinel, then cascade parents
      result = newValues.filter(id => id !== 0);
      const prevWithoutAll = this.prevSelectedSectionIds.filter(id => id !== 0);

      const added = result.filter(id => !prevWithoutAll.includes(id));
      const removed = prevWithoutAll.filter(id => !result.includes(id));

      for (const id of added) {
        if (this.isParentSection(id)) {
          for (const childId of this.getChildSectionIds(id)) {
            if (!result.includes(childId)) {
              result.push(childId);
            }
          }
        }
      }

      for (const id of removed) {
        if (this.isParentSection(id)) {
          const childIds = this.getChildSectionIds(id);
          result = result.filter(rid => !childIds.includes(rid));
        }
      }

      // If all sections are now selected, add ALL
      if (result.length === this.allSectionIds.length && this.allSectionIds.length > 0) {
        result = [0, ...result];
      }
    }

    this.selectedNotificationSectionIds = result;
    this.prevSelectedSectionIds = [...result];
    this.prevAllSectionsToggled = result.includes(0);
  }

  private isParentSection(id: number): boolean {
    return this.availableSections.some(s => s.id === id && !s.parent_id);
  }

  private getChildSectionIds(parentId: number): number[] {
    return this.availableSections
      .filter(s => s.parent_id === parentId && s.can_report)
      .map(s => s.id);
  }

  public isAllSectionsSelected(): boolean {
    return this.selectedNotificationSectionIds.includes(0);
  }

  public getSectionDisplayText(): string {
    if (this.isAllSectionsSelected()) {
      return 'All sections';
    }
    return this.availableSections
      .filter(s => this.selectedNotificationSectionIds.includes(s.id))
      .map(s => s.name)
      .join(', ');
  }

  public updatePreferences() {
    this.user.update_email_preference = this.notificationFrequency;
    // If "All sections" is selected (0), send empty array to API
    this.user.notification_sections = this.selectedNotificationSectionIds.includes(0)
      ? []
      : this.selectedNotificationSectionIds.filter(id => id !== 0);
    // If all countries are selected, send empty array to API
    this.user.notification_countries = this.selectedNotificationCountries.length === this.availableCountries.length
      ? []
      : this.selectedNotificationCountries.map(c => c.iso2);
    this.userService.saveUser(this.user).subscribe(
      () => {
        this.error = '';
        this.snackBar.open('Preferences updated!', 'OK');
      },
      () => {
        this.error = 'Unable to update preferences';
      }
    );
  }

  constructor(
    public userService: UserService,
    public snackBar: MatSnackBar,
    private mixpanelService: MixpanelService,
    private documentService: DocumentService,
    private countryService: CountryService
  ) { }

  ngOnInit() {
    this.userService.currentUser()?.subscribe((user) => {
      this.notificationFrequency = user.update_email_preference;
      this.user = user;
      this.initSectionSelection();
      this.initCountrySelection();
    });
    this.documentService.getReportableSectionBaselines().subscribe(sections => {
      this.availableSections = sections;
      this.allSectionIds = sections
        .filter(s => !s.parent_id || s.can_report)
        .map(s => s.id);
      this.initSectionSelection();
    });
    this.countryService.userCountries().subscribe(countries => {
      this.availableCountries = countries;
      this.initCountrySelection();
    });
    this.mixpanelService.track('Page: Preferences');
  }

  private initSectionSelection() {
    if (!this.user || this.allSectionIds.length === 0) return;

    const userSections = this.user.notification_sections;
    if (!userSections || userSections.length === 0) {
      this.selectedNotificationSectionIds = [0, ...this.allSectionIds];
    } else {
      const allSelected = this.allSectionIds.every(id => userSections.includes(id));
      this.selectedNotificationSectionIds = allSelected
        ? [0, ...userSections]
        : [...userSections];
    }
    this.prevSelectedSectionIds = [...this.selectedNotificationSectionIds];
    this.prevAllSectionsToggled = this.selectedNotificationSectionIds.includes(0);
  }

  private initCountrySelection() {
    if (!this.user || this.availableCountries.length === 0) return;

    const userCountryIsos = this.user.notification_countries;
    if (!userCountryIsos || userCountryIsos.length === 0) {
      // Empty from API = all countries
      this.selectedNotificationCountries = [...this.availableCountries];
    } else {
      this.selectedNotificationCountries = this.availableCountries
        .filter(c => userCountryIsos.includes(c.iso2));
    }
  }
}
